import { useEffect, useReducer, useRef, useState } from 'react';
import { KeyboardKey, OptionSelect } from './enums';
import { Option } from '.';

export type UseSelectControlArgsType = {
  options: Option[];
  value?: string;
  handleValue?: (value: string) => void;
};

export type UseSelectControlReturn = {
  dropdownRef: React.MutableRefObject<HTMLDivElement | null>;
  addOptionRef: (optionRef: HTMLLIElement | null) => HTMLLIElement | null;
  dropdownOpen: boolean;
  changeValue: (value: string) => void;
  handleKeyboardControls: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  toggleDropdownOpen: React.DispatchWithoutAction;
  selectedValue: string;
  mouseHover: boolean;
  setMouseHover: React.Dispatch<React.SetStateAction<boolean>>;
  isKeyboardControlStarted: boolean;
  preSelectedValue: string;
};

/**
 * A hook to provide functionality for a dropdown input.
 *
 * @param options - the provided options for the dropdown
 * @param value (optional) - the shown value in the input, take priority over the inner one if added
 * @param handleValue (optional) - a function that controls the outcome of selecting a value. If completed, it's used as an extension for `changeValue`
 * 
 * @returns object with properties:
 *  - dropdownRef - the ref that should be assigned to the overall dropdown container
 *  - addOptionRef - a function for making a ref for each provided option
 *  - dropdownOpen - to determine whether the dropdown is open or not
 *  - toggleDropdownOpen - a function that toggles the dropdown
 *  - changeValue - a function that changes the selected value
 *  - handleKeyboardControls - a function that determines the behaviour of the keyboard navigation
 *  - selectedValue - the value of the selected option
 *  - mouseHover, setMouseHover - to determine whether the mouse is placed over an option. Upon pressing a key, it becomes false
 *  - isKeyboardControlStarted - to determine whether the user started using keyboard navigation. Upon moving the mouse, it becomes false
 *  - preSelectedValue - to determine what option to be highlighted
 *
 * @example
 * const { dropdownRef, addOptionRef, dropdownOpen, toggleDropdownOpen, changeValue, handleKeyboardControls, selectedValue } = useSelectControl(
    {options: [{
        name: 'Option 1',
        value: 'option-1'
      },
      {
        name: 'Option 2',
        value: 'option-2'
      }]
    }
  );
 */
const useSelectControl = ({
  options,
  value: customValue,
  handleValue: handleCustomValue,
}: UseSelectControlArgsType): UseSelectControlReturn => {
  const TYPEAHEAD_CLEAR_TIMEOUT = 500;

  const [selectedValue, setSelectedValue] = useState<string>(customValue ?? '');
  const [dropdownOpen, toggleDropdownOpen] = useReducer((state) => !state, false);
  const [mouseHover, setMouseHover] = useState<boolean>(false);
  const [isKeyboardControlStarted, setKeyboardControl] = useState<boolean>(false);
  const [preSelectedValue, setPreSelectedValue] = useState<string>('');
  const [typeaheadValue, setTypeaheadValue] = useState<string>('');
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const optionsRef = useRef<(HTMLLIElement | null)[]>([]);

  const scrollToTargetOption = (index: number): void => {
    const targetOptionRef = optionsRef.current[index];
    if (targetOptionRef && targetOptionRef !== null) {
      targetOptionRef?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start',
      });
    }
  };

  const findOptionIndexByValue = (targetValue: string): number => {
    const suggestedOption = options.find(({ value }) => targetValue === value);

    if (!suggestedOption) {
      return 0;
    }

    return options.indexOf(suggestedOption);
  };

  // -1 is the equivalent of 'Please select' - a temporary value until the user selects something
  const [, selectOptionByKeyboard] = useReducer(
    (state: number, action: { type: string; payload?: number }) => {
      const { NEXT, PREVIOUS, LAST, FIRST, UPDATE } = OptionSelect;
      let nextNumber = -1;
      switch (action.type) {
        case NEXT:
          nextNumber = state >= options.length - 1 ? 0 : state + 1;
          break;
        case PREVIOUS:
          nextNumber = state <= 0 ? options.length - 1 : state - 1;
          break;
        case LAST:
          nextNumber = options.length - 1;
          break;
        case FIRST:
          nextNumber = 0;
          break;
        case UPDATE:
          nextNumber = action.payload ?? 0;
          break;
      }
      setPreSelectedValue(options[nextNumber].value);
      scrollToTargetOption(nextNumber);
      return nextNumber;
    },
    -1
  );

  //  Used to keep count of the options so that we can scroll to them if outside of list's view
  // when we select by keyboard
  const addOptionRef = (optionRef: HTMLLIElement | null): HTMLLIElement | null => {
    if (optionRef == null) {
      return null;
    }

    if (optionsRef.current.find((optionR) => optionR === optionRef)) {
      return null;
    }

    optionsRef.current.push(optionRef);
    return optionRef;
  };

  const changeValue = (value: string): void => {
    setSelectedValue(value);
    toggleDropdownOpen();
    setMouseHover(false);
    handleCustomValue?.(value);
  };

  const handleClosingOnKeyboard = (key: string): void => {
    const { UPDATE } = OptionSelect;

    // Upon pressing Esc, it should close the dropdown and bring the preselected value back to the currently selected value
    if (key === KeyboardKey.ESC && dropdownOpen) {
      toggleDropdownOpen();
      setPreSelectedValue(selectedValue);
      selectOptionByKeyboard({
        type: UPDATE,
        payload: findOptionIndexByValue(selectedValue),
      });
    }

    // Upon pressing Enter, it should open the dropdown or select the preselected value and close the dropdown if already open
    if (key === KeyboardKey.ENTER) {
      toggleDropdownOpen();

      if (dropdownOpen) {
        if (preSelectedValue && preSelectedValue !== '') {
          changeValue(preSelectedValue);
        }
        toggleDropdownOpen();
      }
    }
  };

  const handleValuePreselectionOnKeyboard = (
    e: React.KeyboardEvent<HTMLDivElement>
  ): void => {
    const { ARROW_UP, ARROW_DOWN, ARROW_LEFT, ARROW_RIGHT, END, HOME } = KeyboardKey;
    if (!dropdownOpen) {
      toggleDropdownOpen();
    }
    e.preventDefault();

    switch (e.key) {
      case ARROW_UP:
      case ARROW_LEFT:
        selectOptionByKeyboard({ type: OptionSelect.PREVIOUS });
        break;
      case ARROW_DOWN:
      case ARROW_RIGHT:
        selectOptionByKeyboard({ type: OptionSelect.NEXT });
        break;
      case END:
        selectOptionByKeyboard({ type: OptionSelect.LAST });
        break;
      case HOME:
        selectOptionByKeyboard({ type: OptionSelect.FIRST });
        break;
    }
  };

  const typeaheadSearch = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    const { UPDATE } = OptionSelect;
    e.preventDefault();
    const searchValue = typeaheadValue + e.key;
    setTypeaheadValue((state) => state + e.key);

    const suggestedOptions = options.filter(({ name }) =>
      name.toLocaleLowerCase().includes(searchValue.toLowerCase())
    );

    if (!suggestedOptions.length) {
      return;
    }

    setPreSelectedValue(suggestedOptions[0].value);
    const optionIndex = options.indexOf(suggestedOptions[0]);
    scrollToTargetOption(optionIndex);
    selectOptionByKeyboard({ type: UPDATE, payload: optionIndex });

    setTimeout(() => {
      setTypeaheadValue('');
    }, TYPEAHEAD_CLEAR_TIMEOUT);
  };

  // Keyboard controls:
  //   - Enter - Select & Close
  //   - Esc - Close
  //   - Arrow Up / Left - Select previous value
  //   - Arrow down / Right - Select next value
  //   - End - Select last value
  //   - Home - Select first value
  //   - A-Z, 1-0, Space - Typeahead Search

  // When dropdown selected, the controls should be limited to these options
  const handleKeyboardControls = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    const {
      ENTER,
      ESC,
      ARROW_UP,
      ARROW_DOWN,
      ARROW_LEFT,
      ARROW_RIGHT,
      END,
      HOME,
    } = KeyboardKey;
    setKeyboardControl(true);
    setMouseHover(false);
    switch (e.key) {
      case ENTER:
      case ESC:
        handleClosingOnKeyboard(e.key);
        break;

      case ARROW_DOWN:
      case ARROW_UP:
      case ARROW_LEFT:
      case ARROW_RIGHT:
      case END:
      case HOME:
        handleValuePreselectionOnKeyboard(e);
        break;
    }

    if (
      e.code === `Key${e.key.toUpperCase()}` ||
      e.code === `Digit${e.key}` ||
      e.key === ' '
    ) {
      typeaheadSearch(e);
    }
  };

  // Closes the dropdown when clicked outside of it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Node;
      if (dropdownRef.current && !dropdownRef.current.contains(target) && dropdownOpen) {
        toggleDropdownOpen();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef, dropdownOpen]);

  // Added a listener when user starts navigating via keyboard to avoid highlight conflict when mouse is left over dropdown list
  useEffect(() => {
    if (!isKeyboardControlStarted) {
      return;
    }

    const handleMouseMove = (): void => {
      setKeyboardControl(false);
    };
    document.addEventListener('mousemove', handleMouseMove);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isKeyboardControlStarted]);

  return {
    dropdownRef,
    addOptionRef,
    dropdownOpen,
    changeValue,
    mouseHover,
    preSelectedValue,
    selectedValue,
    handleKeyboardControls,
    toggleDropdownOpen,
    setMouseHover,
    isKeyboardControlStarted,
  };
};

export default useSelectControl;
